import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  Typography,
  Box,
  CircularProgress,
} from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import { useAuth } from "src/context/auth";
import { useNotifications } from "@toolpad/core/useNotifications";
import { getRoutes } from "src/utilities/routes";
import { handleSendMessage } from "src/utilities";
import * as XLSX from "xlsx";
import FileDownloadIcon from "@mui/icons-material/FileDownload";

interface ServiceInfo {
  serviceId: number;
  serviceName: string;
}

interface CustomerServiceReport {
  customerId: string;
  customerName: string;
  companyName: string;
  serviceUsage: { [key: number]: boolean };
}

interface AgreementServiceReport {
  customers: CustomerServiceReport[];
  services: ServiceInfo[];
}

interface AgreementServiceReportDialogProps {
  open: boolean;
  onClose: () => void;
}

const AgreementServiceReportDialog: React.FC<
  AgreementServiceReportDialogProps
> = ({ open, onClose }) => {
  const { user } = useAuth();
  const notifications = useNotifications();
  const Routes = getRoutes();
  const [reportData, setReportData] = useState<AgreementServiceReport | null>(
    null
  );

  const { mutate: getReport, isPending: isLoading } = useMutation({
    mutationFn: async () => {
      const response = await fetch(`${Routes.AgreementsRoutes}service-report`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${user?.token}`,
          Accept: "application/json",
        },
      });
      return await response.json();
    },
    onSuccess: (responseData: any) => {
      if (responseData.status) {
        setReportData(responseData.data);
      } else {
        handleSendMessage(notifications, "خطا در دریافت گزارش", "error");
      }
    },
    onError: (error) => {
      handleSendMessage(notifications, "خطا در دریافت گزارش", "error");
      console.log(error);
    },
  });

  useEffect(() => {
    if (open) {
      getReport();
    }
  }, [open]);

  const handleClose = () => {
    setReportData(null);
    onClose();
  };

  const handleExportToExcel = () => {
    if (!reportData) return;

    try {
      // آماده‌سازی داده‌ها برای صادرات
      const exportData = reportData.customers.map((customer) => {
        const row: any = {
          "نام مشتری": customer.customerName,
          "نام شرکت": customer.companyName,
        };

        // اضافه کردن ستون‌های خدمات
        reportData.services.forEach((service) => {
          row[service.serviceName] = customer.serviceUsage[service.serviceId]
            ? "✓"
            : "✗";
        });

        return row;
      });

      // ایجاد workbook و worksheet
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(exportData);

      // تنظیم عرض ستون‌ها
      const colWidths = [
        { wch: 20 }, // نام مشتری
        { wch: 25 }, // نام شرکت
        ...reportData.services.map(() => ({ wch: 15 })), // ستون‌های خدمات
      ];
      ws["!cols"] = colWidths;

      // اضافه کردن worksheet به workbook
      XLSX.utils.book_append_sheet(wb, ws, "گزارش خدمات قراردادها");

      // تولید نام فایل با تاریخ
      const now = new Date();
      const dateStr = now.toLocaleDateString("fa-IR").replace(/\//g, "-");
      const timeStr = now.toLocaleTimeString("fa-IR").replace(/:/g, "-");
      const fileName = `گزارش_خدمات_قراردادها_${dateStr}_${timeStr}.xlsx`;

      // دانلود فایل
      XLSX.writeFile(wb, fileName);

      handleSendMessage(notifications, "فایل با موفقیت صادر شد", "success");
    } catch (error) {
      console.error("خطا در صادرات فایل:", error);
      handleSendMessage(notifications, "خطا در صادرات فایل", "error");
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: {
          minHeight: "80vh",
        },
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" component="div">
            گزارش خدمات قراردادها
          </Typography>
          {reportData && (
            <Button
              variant="contained"
              color="success"
              startIcon={<FileDownloadIcon />}
              onClick={handleExportToExcel}
              disabled={isLoading}
            >
              صادرات به Excel
            </Button>
          )}
        </Box>
      </DialogTitle>
      <DialogContent>
        {isLoading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="400px"
          >
            <CircularProgress />
          </Box>
        ) : reportData ? (
          <TableContainer component={Paper} sx={{ maxHeight: "70vh" }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      backgroundColor: "primary.main",
                      color: "white",
                      fontWeight: "bold",
                      minWidth: 200,
                    }}
                  >
                    نام مشتری
                  </TableCell>
                  <TableCell
                    sx={{
                      backgroundColor: "primary.main",
                      color: "white",
                      fontWeight: "bold",
                      minWidth: 150,
                    }}
                  >
                    نام شرکت
                  </TableCell>
                  {reportData.services.map((service) => (
                    <TableCell
                      key={service.serviceId}
                      sx={{
                        backgroundColor: "primary.main",
                        color: "white",
                        fontWeight: "bold",
                        textAlign: "center",
                        minWidth: 120,
                      }}
                    >
                      {service.serviceName}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {reportData.customers.map((customer) => (
                  <TableRow key={customer.customerId} hover>
                    <TableCell sx={{ fontWeight: "medium" }}>
                      {customer.customerName}
                    </TableCell>
                    <TableCell>{customer.companyName}</TableCell>
                    {reportData.services.map((service) => (
                      <TableCell key={service.serviceId} align="center">
                        <Checkbox
                          checked={
                            customer.serviceUsage[service.serviceId] || false
                          }
                          disabled
                          sx={{
                            "&.Mui-checked": {
                              color: "success.main",
                            },
                          }}
                        />
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Typography variant="body1" color="text.secondary" textAlign="center">
            هیچ داده‌ای برای نمایش وجود ندارد
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="primary">
          بستن
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AgreementServiceReportDialog;
